/**
 * spriteFingerprintCore - shared fingerprint math used by both
 * `scripts/generate-sprite-fingerprints.ts` (build-time, sharp-decoded
 * RGBA from PokeAPI PNGs) and the runtime via
 * `client/src/lib/cellSpriteMatcher.ts` (live cell crops).
 *
 * Keeping these computations in one place is non-negotiable: a bin shift
 * or a chroma threshold drift between generator and matcher would silently
 * break every match. Consumers: the generator script, and (since 2026-08-20)
 * the runtime shiny classifier `myPokemonShiny.ts`, which fingerprints the
 * caught-page sprite crop and compares it to the bundled base/shiny pair.
 *
 * Mirrors `computeHueHistogram` in cellSpriteMatcher.ts. When that
 * function is edited, this one must be kept in lockstep. Both are pure.
 */

export const HUE_BINS = 8;
const POGO_BG_R = 246;
const POGO_BG_G = 190;
const POGO_BG_B = 177;
const BG_DIST_SQ = 60 * 60;
const CHROMA_RGB_SPAN = 24;

export interface SpriteFingerprintJson {
  hueHistogram: number[];
  chromaFraction: number;
  /** Circular-mean hue in degrees [0,360), or null when chromaFraction === 0.
   *  JSON cannot represent NaN, so the generator serialises the no-chroma
   *  case as null. Consumers must tolerate null. */
  dominantHue: number | null;
}

export function buildFingerprint(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  /** PokeAPI sprites have transparent backgrounds and no PoGo pink, so
   *  the generator passes false. The runtime cell crops pass true. */
  excludePogoBackground = false,
  /**
   * Runtime-only adaptive page-background exclusion. The fixed POGO_BG pink is
   * the ONLY tint the main-dex used to sit on, but PoGo region pages also come
   * in CYAN (pokemon/xxl/xxs), golden, lavender, etc. A cyan page is NOT near
   * POGO_BG, so its background pixels flood the cell histogram and drown the
   * small sprites real hue (device-proven: chromaFraction ~0.97, cosine ~0
   * vs the correct green reference, every cell read "uncertain" -> dropped ->
   * scan stuck on "Hold steady"). When the caller measures the cells own
   * background (the outer-ring median via estimateLocalCellBackground) and
   * passes it here, pixels within BG_DIST_SQ of THAT colour are also excluded,
   * so the sprites hue survives on any page tint.
   *
   * Generator-safe: the build-time generator calls buildFingerprint with only
   * (data, width, height) and never passes this, so reference generation is
   * byte-identical. Only consulted when excludePogoBackground is also true.
   */
  extraBgExclude?: { r: number; g: number; b: number } | null,
): SpriteFingerprintJson {
  if (width < 2 || height < 2 || data.length < 16) {
    return {
      hueHistogram: new Array(HUE_BINS).fill(0),
      chromaFraction: 0,
      dominantHue: null,
    };
  }
  const bins = new Array<number>(HUE_BINS).fill(0);
  // Parallel histogram that ALWAYS bins every chromatic pixel, ignoring the
  // adaptive page-tint exclusion. Used only as a safety fallback: if the
  // adaptive exclusion removes EVERY chromatic pixel (binned === 0), the colour
  // we measured as "background" was actually the cell content (a sprite that
  // fills the whole crop, e.g. synthetic/reconstructed reference cells with no
  // distinct page border), so excluding it would empty the histogram and force
  // a false "uncertain". In that degenerate case we revert to this unexcluded
  // histogram. A real page flood always leaves the sprite body (a DIFFERENT
  // hue) behind, so binned > 0 and the fallback never fires for it.
  const binsAll = new Array<number>(HUE_BINS).fill(0);
  let chromatic = 0;
  let binned = 0;
  let total = 0;
  let cosSum = 0,
    sinSum = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a < 16) continue;
    const r = data[i],
      g = data[i + 1],
      b = data[i + 2];
    if (excludePogoBackground) {
      const dr = r - POGO_BG_R,
        dg = g - POGO_BG_G,
        db = b - POGO_BG_B;
      if (dr * dr + dg * dg + db * db <= BG_DIST_SQ) continue;
    }
    // Adaptive page-tint exclusion: when the caller supplies the cells own
    // measured background (cyan/golden/lavender region pages, etc.), pixels near
    // that tint are dropped from the HUE HISTOGRAM ONLY so the small sprites
    // real hue is not flooded by the page colour. Same radius as the pink gate.
    //
    // SCOPED ON PURPOSE: this exclusion is applied ONLY to the histogram bins
    // (the cosine-match vector). `total`, `chromatic`, `chromaFraction`, and
    // `dominantHue` are left on the original pink-only basis, because the
    // presence / flood-ceiling gates downstream (globalChromaPresent,
    // PRESENCE_CHROMA_FLOOD_CEILING, empty-cell demotion) are tuned against that
    // chromaFraction and must not shift. A real sprite lives in the cell interior
    // and is not dominated by the border-ring colour, so its body pixels survive
    // the histogram exclusion; only the page flood is removed.
    let nearExtraBg = false;
    if (excludePogoBackground && extraBgExclude) {
      const er = r - extraBgExclude.r,
        eg = g - extraBgExclude.g,
        eb = b - extraBgExclude.b;
      if (er * er + eg * eg + eb * eb <= BG_DIST_SQ) nearExtraBg = true;
    }
    total++;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    if (d < CHROMA_RGB_SPAN) continue;
    chromatic++;
    let h: number;
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    if (h < 0) h += 6;
    const radians = (h / 6) * Math.PI * 2;
    cosSum += Math.cos(radians);
    sinSum += Math.sin(radians);
    const bin = Math.min(
      HUE_BINS - 1,
      Math.max(0, Math.floor((h / 6) * HUE_BINS)),
    );
    // `binsAll` always receives every chromatic pixel (the unexcluded vector,
    // used only for the binned===0 degenerate-fallback below).
    binsAll[bin]++;
    // Histogram-only page-tint exclusion: chromaFraction/dominantHue keep this
    // pixel (counted in `chromatic`/`cosSum`/`sinSum` above) so the presence and
    // flood gates are unchanged; only the cosine-match vector drops it.
    if (nearExtraBg) continue;
    bins[bin]++;
    binned++;
  }
  if (chromatic === 0) {
    return {
      hueHistogram: bins,
      chromaFraction: 0,
      dominantHue: null,
    };
  }
  // Normalise the histogram by the number of pixels actually binned (which is
  // `chromatic` when no extraBgExclude was applied, or fewer when page-tint
  // pixels were dropped from the bins). Keeps the histogram a proper L1
  // distribution so the active-bin mass floors (ACTIVE_HUE_BIN_MASS) behave the
  // same whether or not the adaptive exclusion fired.
  // Degenerate-fallback guard: if the adaptive page-tint exclusion removed
  // EVERY chromatic pixel (binned === 0), the measured background WAS the cell
  // content (sprite fills the crop, no distinct page border). Excluding it would
  // leave an all-zero histogram and force a false "uncertain" — the regression
  // that broke solid/reconstructed-reference cells. Revert to the unexcluded
  // histogram so those cells match exactly as before. A genuine page flood
  // always leaves the sprite body (a different hue) behind => binned > 0 => this
  // branch never fires for it, preserving the cyan-flood fix.
  if (binned === 0 && extraBgExclude) {
    for (let i = 0; i < HUE_BINS; i++) bins[i] = binsAll[i];
  }
  const binNorm = binned > 0 ? binned : chromatic;
  for (let i = 0; i < HUE_BINS; i++) bins[i] /= binNorm;
  const meanRad = Math.atan2(sinSum / chromatic, cosSum / chromatic);
  let dominantHue = (meanRad * 180) / Math.PI;
  if (dominantHue < 0) dominantHue += 360;
  return {
    hueHistogram: bins,
    chromaFraction: total > 0 ? chromatic / total : 0,
    dominantHue,
  };
}


// ── Palettes (2026-09-18) ────────────────────────────────────────────────────
//
// The 8-bin hue histogram above cannot tell a shade from its neighbour, which
// is what most shiny palettes are (358 of 1025 species score "too similar" on
// hue alone), and it drowns under a page scene the runtime can only subtract
// one colour of. A PALETTE is the sprite's few dominant colours with their
// weights; the shiny classifier votes on colours that belong to only one of
// the two references and masks the scene with the crop's own border palette.
// Shared by the generator (reference art, transparent background) and the
// runtime (a page crop), so the two cannot drift.

/** [r, g, b, weight] with weight in 0..1000 across the palette. */
export type PaletteColour = [number, number, number, number];

/** Colours closer than this (redmean distance) are one palette entry. */
export const PALETTE_MERGE_DISTANCE = 32;
/** Palette entries kept per reference. */
export const PALETTE_SIZE = 8;

/**
 * Perceptually weighted RGB distance ("redmean"): cheap, and close enough to
 * a Lab distance for telling one palette colour from another.
 */
export function colourDistance(
  r1: number, g1: number, b1: number,
  r2: number, g2: number, b2: number,
): number {
  const rm = (r1 + r2) / 2;
  const dr = r1 - r2;
  const dg = g1 - g2;
  const db = b1 - b2;
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}

/**
 * The dominant colours of an RGBA buffer: pixels quantised to a 16-level grid,
 * the grid cells merged greedily by distance, the heaviest `k` kept. Pixels
 * with alpha under `alphaMin`, and pixels within `excludeDistance` of any
 * `exclude` colour, are ignored (the runtime passes the scene's colours).
 */
export function extractPalette(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  opts: {
    k?: number;
    stride?: number;
    alphaMin?: number;
    exclude?: readonly PaletteColour[];
    excludeDistance?: number;
    /** Restrict to a sub-rectangle predicate, e.g. the border ring. */
    within?: (x: number, y: number) => boolean;
  } = {},
): PaletteColour[] {
  const k = opts.k ?? PALETTE_SIZE;
  const stride = Math.max(1, opts.stride ?? 1);
  const alphaMin = opts.alphaMin ?? 128;
  const exclude = opts.exclude ?? [];
  const excludeDistance = opts.excludeDistance ?? PALETTE_MERGE_DISTANCE;
  // 16 levels per channel: 4096 cells of count + colour sums.
  const cells = new Float64Array(4096 * 4);
  let total = 0;
  for (let y = 0; y < height; y += stride) {
    for (let x = 0; x < width; x += stride) {
      if (opts.within && !opts.within(x, y)) continue;
      const i = (y * width + x) * 4;
      if (data[i + 3] < alphaMin) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      let skip = false;
      for (let e = 0; e < exclude.length; e++) {
        const c = exclude[e];
        if (colourDistance(r, g, b, c[0], c[1], c[2]) <= excludeDistance) { skip = true; break; }
      }
      if (skip) continue;
      const cell = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      const o = cell * 4;
      cells[o] += 1;
      cells[o + 1] += r;
      cells[o + 2] += g;
      cells[o + 3] += b;
      total += 1;
    }
  }
  if (total === 0) return [];
  // Cells by weight, heaviest first.
  const order: number[] = [];
  for (let c = 0; c < 4096; c++) if (cells[c * 4] > 0) order.push(c);
  order.sort((a, b) => cells[b * 4] - cells[a * 4]);
  // Greedy merge into clusters.
  const clusters: Array<{ n: number; r: number; g: number; b: number }> = [];
  for (const c of order) {
    const n = cells[c * 4];
    const r = cells[c * 4 + 1] / n;
    const g = cells[c * 4 + 2] / n;
    const b = cells[c * 4 + 3] / n;
    let merged = false;
    for (const cl of clusters) {
      if (colourDistance(r, g, b, cl.r / cl.n, cl.g / cl.n, cl.b / cl.n) <= PALETTE_MERGE_DISTANCE) {
        cl.n += n; cl.r += r * n; cl.g += g * n; cl.b += b * n;
        merged = true;
        break;
      }
    }
    if (!merged) clusters.push({ n, r: r * n, g: g * n, b: b * n });
  }
  clusters.sort((a, b) => b.n - a.n);
  const top = clusters.slice(0, k);
  const kept = top.reduce((sum, cl) => sum + cl.n, 0) || 1;
  return top.map((cl) => [
    Math.round(cl.r / cl.n),
    Math.round(cl.g / cl.n),
    Math.round(cl.b / cl.n),
    Math.round((cl.n / kept) * 1000),
  ]);
}
